import { useEffect, useRef } from 'react';
import type { Presentation } from '../../types';
import { DeepLinkCta } from './DeepLinkCta';

interface Props {
  p: Extract<Presentation, { type: 'map' }>;
}

// Leaflet integration is implemented imperatively (rather than via react-leaflet)
// because the Leaflet imports break SSR/JSDOM and tree-shaking, and this gives
// us a smaller bundle with cleaner failure modes when Leaflet isn't available
// (renders a clear placeholder instead of crashing the whole answer card).
export function MapPresentation({ p }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let map: { remove: () => void } | null = null;
    let cancelled = false;

    (async () => {
      if (!ref.current || typeof window === 'undefined') return;
      try {
        const L = (await import('leaflet')).default;
        if (cancelled || !ref.current) return;
        map = L.map(ref.current, {
          center: p.center,
          zoom: p.zoom,
          scrollWheelZoom: false,
        });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
          maxZoom: 18,
        }).addTo(map as L.Map);
        const safeGeo = {
          type: 'FeatureCollection' as const,
          features: Array.isArray(p.geojson?.features) ? p.geojson.features : [],
        };
        const geoLayer = L.geoJSON(safeGeo as GeoJSON.GeoJsonObject, {
          style: { color: '#005ea2', weight: 2, fillOpacity: 0.2 },
          onEachFeature: (feature, layer) => {
            const props = feature.properties as Record<string, unknown> | undefined;
            if (!props) return;
            // Build the popup as DOM nodes — bindPopup(string) sets innerHTML,
            // which would execute any markup in upstream property values. Using
            // textContent / createTextNode keeps everything text-only.
            const el = document.createElement('div');
            Object.entries(props).slice(0, 5).forEach(([k, v]) => {
              const row = document.createElement('p');
              const key = document.createElement('strong');
              key.textContent = k;
              const val = document.createTextNode(`: ${v}`);
              row.appendChild(key);
              row.appendChild(val);
              el.appendChild(row);
            });
            layer.bindPopup(el.children.length ? el : 'Feature');
          },
        }).addTo(map as L.Map);

        // Auto-fit the viewport to the actual feature bounds so a query for
        // (e.g.) "health centers in Chicago" zooms into Chicago instead of
        // showing the whole continental US. Falls back to the
        // backend-supplied center+zoom (set in the constructor above) if
        // the layer has no derivable bounds — a common case for empty
        // result sets or for prose-cum-map presentations that just want
        // a regional overview.
        try {
          const bounds = geoLayer.getBounds();
          if (bounds.isValid()) {
            (map as L.Map).fitBounds(bounds, {
              padding: [24, 24],   // px; keep markers off the edge of the canvas
              maxZoom: 14,         // street-level detail without losing context
              animate: false,
            });
          }
        } catch {
          // getBounds() throws on degenerate layers (no features, all-null
          // geometries). Silent fall-through to the backend's center+zoom is
          // the right behavior — the user still sees a sensible map.
        }
      } catch (err) {
        if (ref.current) {
          ref.current.innerText = `Map unavailable: ${(err as Error).message}`;
        }
      }
    })();

    return () => {
      cancelled = true;
      map?.remove();
    };
  }, [p.geojson, p.center, p.zoom]);

  // GeoJSON shape is loosely validated upstream — features can be null or
  // missing if the backend emits a malformed envelope. Compute the count
  // defensively so the title doesn't crash the whole subtree.
  const featureCount = Array.isArray(p.geojson?.features) ? p.geojson.features.length : 0;
  return (
    <div className="presentation-card">
      <div className="presentation-title">🗺 Map ({featureCount} features)</div>
      <div ref={ref} className="map-canvas" style={{ height: 360 }} />
      {p.cta && <DeepLinkCta cta={p.cta} />}
    </div>
  );
}
